import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Plus, X, Target } from "lucide-react";
import { toast } from "sonner";

const cats = ["emergency", "debt", "house", "retirement", "other"];

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ title: "", target: "", current: "0", category: "emergency" });

  const load = async () => setGoals((await api.get("/goals")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/goals", { ...f, target: parseFloat(f.target), current: parseFloat(f.current) });
    toast.success("Goal added");
    setShow(false); setF({ title: "", target: "", current: "0", category: "emergency" });
    load();
  };

  const del = async (id) => { await api.delete(`/goals/${id}`); load(); };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Goals</h1>
          <p className="text-[color:var(--text-secondary)] mt-1">The plan adjusts weekly.</p>
        </div>
        <button data-testid="goals-tracker-add-goal-button" onClick={() => setShow(true)} className="gold-btn px-5 py-2.5 rounded-full inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New goal
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {goals.map((g) => {
          const pct = Math.min(100, (g.current / g.target) * 100);
          return (
            <motion.div key={g.goal_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[rgba(212,175,55,0.1)] flex items-center justify-center">
                    <Target className="w-5 h-5 text-[color:var(--gold)]" />
                  </div>
                  <div>
                    <div className="font-heading text-lg font-semibold">{g.title}</div>
                    <div className="text-xs text-[color:var(--text-muted)] uppercase tracking-widest">{g.category}</div>
                  </div>
                </div>
                <button data-testid={`delete-goal-${g.goal_id}`} onClick={() => del(g.goal_id)} className="text-[color:var(--text-muted)] hover:text-[color:var(--terracotta)]">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="mb-3 flex justify-between font-mono text-sm">
                <span className="gold-gradient-text font-semibold">${g.current.toLocaleString()}</span>
                <span className="text-[color:var(--text-muted)]">/ ${g.target.toLocaleString()}</span>
              </div>
              <div className="h-2 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1 }}
                  className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E9C46A]" />
              </div>
              <div className="mt-2 text-xs text-[color:var(--text-secondary)]">{pct.toFixed(0)}% complete</div>
            </motion.div>
          );
        })}
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <motion.form initial={{ scale: 0.9 }} animate={{ scale: 1 }} onSubmit={submit}
            className="glass-card p-8 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
            <h2 className="font-heading text-2xl font-bold">New goal</h2>
            <input data-testid="goal-title-input" required placeholder="Title (e.g. Vacation Fund)" value={f.title} onChange={e => setF({ ...f, title: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="goal-target-input" required type="number" placeholder="Target $" value={f.target} onChange={e => setF({ ...f, target: e.target.value })}
                className="px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none" />
              <input data-testid="goal-current-input" type="number" placeholder="Current $" value={f.current} onChange={e => setF({ ...f, current: e.target.value })}
                className="px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none" />
            </div>
            <select data-testid="goal-category-select" value={f.category} onChange={e => setF({ ...f, category: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] outline-none">
              {cats.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button data-testid="goal-submit-btn" type="submit" className="gold-btn w-full py-3 rounded-xl">Add goal</button>
          </motion.form>
        </div>
      )}
    </div>
  );
}
